/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { AppError } from "@tivocorp/data-domain-lib";

import { Locale, LocaleKeys } from "../locale";
import { ErrorCodeUtil } from "../utility/ErrorCodeUtil";
import { AxiosErrorAdapter } from "./adapters";
import type { IErrorAdapter } from "./adapters";

/** Shape a raw thrown value may match, as far as this mapper cares. */
interface ErrorLike {
  message?: unknown;
  getMessage?: () => unknown;
}

/** Narrows `unknown` to the subset of {@link ErrorLike} present on it. */
function asErrorLike(error: unknown): ErrorLike {
  return typeof error === "object" && error !== null
    ? (error as ErrorLike)
    : {};
}

/**
 * Best available diagnostic text for a thrown error: an `AppError`'s
 * message — those do not extend `Error`, so `error.message` alone would miss
 * them — then a plain `Error.message`, then empty. Shared by every mapper
 * this file builds, per-screen mappers (`HomeScreenErrorMapper`,
 * `ContentDetailErrorMapper`) included.
 */
function extractMessage(error: unknown): string {
  const errorLike: ErrorLike = asErrorLike(error);
  if (typeof errorLike.getMessage === "function") {
    const message: unknown = errorLike.getMessage();
    if (typeof message === "string" && message.length > 0) {
      return message;
    }
  }
  return typeof errorLike.message === "string" ? errorLike.message : "";
}

/** A code-to-AppError mapper built by {@link createActionErrorMapper}. */
export interface ActionErrorMapper {
  /**
   * Maps a raw error to an `AppError` carrying this mapper's code and the
   * appropriate sub-error suffix — see {@link ErrorCodeUtil} for the suffix
   * families (N6xx transport, S6xx service context, G4xx-G6xx HTTP status).
   * A sub-code is never guessed: no layer recognising the error leaves it
   * empty rather than showing a near-enough code.
   */
  fromError(error: unknown): AppError;
  /** Composite code for display: `"<code>-G500"`, or bare `"<code>"`. */
  toCompositeCode(error: unknown): string;
  /**
   * `message` with the composite code appended in parentheses (e.g.
   * `"Your request cannot be processed (R323-G500)"`) — the same "(code)"
   * suffix convention the sign-in terminal alert uses. `error` may be
   * omitted for a failure with no underlying error object (e.g. a
   * malformed-but-200-OK response); the code is then reported bare.
   */
  appendErrorCode(message: string, error?: unknown): string;
}

/**
 * Builds an {@link ActionErrorMapper} for one catalog code — the same
 * fromError/toCompositeCode shape `HomeScreenErrorMapper` and
 * `ContentDetailErrorMapper` hand-roll per screen, factored out so a new
 * per-action code (one per button/API rather than one per screen) is a
 * one-line call instead of a copy-pasted class.
 *
 * Title/body are the same generic connection-issue copy every action-level
 * code shares (`LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_*`) — these
 * codes back toasts, not dialogs, so the copy is never read from the
 * `AppError` itself; only the composite code is.
 *
 * @param code - The catalog code this mapper reports, e.g. `"R323"`
 */
export function createActionErrorMapper(code: string): ActionErrorMapper {
  const adapter: IErrorAdapter = new AxiosErrorAdapter();

  function fromError(error: unknown): AppError {
    const subErrorCode: string = ErrorCodeUtil.extractSubErrorCode(
      error,
      adapter
    );
    return new AppError(
      code,
      extractMessage(error),
      subErrorCode,
      Locale.getString(LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_TITLE),
      Locale.getString(
        LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
      )
    );
  }

  function toCompositeCode(error: unknown): string {
    return fromError(error).getUIErrorwithSubErrorCode();
  }

  function appendErrorCode(message: string, error?: unknown): string {
    return message + " (" + toCompositeCode(error) + ")";
  }

  return { fromError, toCompositeCode, appendErrorCode };
}
